import React, {useCallback, useContext, useEffect, useMemo} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import {ListConfigFieldTypes} from '@src/@types/components/List';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import ControlledList from '@src/components/ControlledList/ControlledList';
import {ControlledListProps} from '@src/@types/components/ControlledList';
import FormField, {Props} from '@src/components/Form/FormField';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {FormTabsContext} from '@src/contexts/FormTabsContext';
import {IExternalApplicationModel} from '@src/@interfaces/models/ExternalApplication/IExternalApplicationModel';
import AddNewTokenButton from '@views/external-applications/fields/AddNewTokenButton';
import app from '@src/helpers/app';
import {stringifyDate} from '@src/helpers/dates';

const columns = [
    {
        name: 'id',
        label: '#',
        type: ListConfigFieldTypes.CUSTOM,
        invisible: true,
    },
    {
        name: 'name',
        label: 'ExternalApplications.fields.token.name',
        type: ListConfigFieldTypes.TEXT,
    },
    {
        name: 'created_at',
        label: 'ExternalApplications.fields.token.created_at',
        type: ListConfigFieldTypes.TIME_TENSE,
    },
    {
        name: 'last_used_at',
        label: 'ExternalApplications.fields.token.last_used_at',
        type: ListConfigFieldTypes.TIME_TENSE,
    },
];

function ActualForm(props: Props<IExternalApplicationModel>) {
    const tabsContext = useContext(FormTabsContext);
    const [designContext] = useContext(FormFieldDesignContext);
    const {control} = useFormContext<IExternalApplicationModel>();
    const {fields, remove, append} = useFieldArray({
        control,
        name: props.fieldName as 'tokens',
        keyName: 'autoGeneratedUuid',
    });

    type Entity = (typeof fields)[number];

    const tabName = props.tabName;
    useEffect(() => {
        if (tabsContext && tabName) {
            tabsContext.setBadge(tabName, {
                color: 'primary',
                label: String(fields.length)
            });
        }
    }, [tabsContext, tabName, fields.length]);

    const isDisabled = props.forceDisabled || designContext.fieldsAreDisabled || designContext.fieldsAreReadOnly;
    const onTokenCreated = useCallback((id: string | number, tokenName: string, abilities: string[]) => {
        append({
            id: +id,
            name: tokenName,
            abilities: abilities,
            created_at: stringifyDate(),
        });
    }, [append]);
    const [listContentProps, formFieldArrayContextValues] = useMemo(() => {
        const listContentProps: ControlledListProps<Entity>['listContentProps'] = {
            tableHeadProps: {
                additionalControlNode: props.model?.id ? (
                        <AddNewTokenButton
                                modelId={props.model?.id}
                                isDisabled={isDisabled}
                                controllerName={props.formName ?? ''}
                                onCreate={onTokenCreated}
                                abilitiesOptions={props.modelOptions?.tokenAbilities}
                        />
                ) : undefined,
            },
            tableBodyProps: {
                renderControls(record, index) {
                    if (!record?.id) {
                        return null;
                    }

                    return <ConfirmableButton
                            size="sm"
                            color="danger"
                            disabled={isDisabled}
                            onClick={() => {
                                app('http').route('external-applications.tokens.destroy', {
                                    id: String(props.model?.id),
                                    tokenId: String(record?.id),
                                }, {
                                    headers: {'content-type': 'application/json'},
                                    body: {'controller-name': props.formName},
                                }).then(() => {
                                    remove(index);
                                });
                            }}
                    >
                        <i className="fa fa-times"/>
                    </ConfirmableButton>;
                }
            }
        };

        const formFieldArrayContextValues: IFormFieldItemPropsContext<IExternalApplicationModel> = {
            control,
            fieldName: props.fieldName,
            formName: props.formName,
            tabName: props.tabName,
            model: props.model,
            modelOptions: props.modelOptions,
            config: props.config,
            forceReadonly: props.config.readOnly === true || props.forceReadonly === true,
            forceDisabled: props.config.disabled === true || props.forceDisabled === true,
        };

        return [listContentProps, formFieldArrayContextValues];
    }, [isDisabled, props, onTokenCreated, control, remove]);

    return <FormFieldItemPropsContext.Provider value={formFieldArrayContextValues as any}>
        <ControlledList<Entity>
                uniqueListName={`${props.formName}@${props.fieldName}--tokens-list`}
                controllerName={props.formName}
                records={fields}
                columns={columns}
                withoutSearchField={true}
                keepInParentBox={true}
                listContentProps={listContentProps}
                bordered={true}
                className="mb-1"
        />
    </FormFieldItemPropsContext.Provider>;
}

export default function ExternalApplicationTokensField<Model extends IExternalApplicationModel = IExternalApplicationModel>(props: Props<Model>) {
    return <FormField {...props} withoutLayout={true} fieldElement={ActualForm as any}/>;
}
